import * as Sentry from '@sentry/gatsby'
import type {
  ContentMenuCardData,
  UseNavbarCardsQuery,
} from 'component-library'
import { gql, GraphQLClient } from 'graphql-request'
import { useEffect, useState } from 'react'
import { buildSearchInput } from 'shared-utilities'
import { getCurrentEnvConfig } from '../../config'
import { handleThumbnail } from '../utils/search'

const graphQLClient = new GraphQLClient(getCurrentEnvConfig().graphql)

const SEARCH_QUERY = gql`
  query SearchQuery($input: SearchInput!, $page: PageInput) {
    search(input: $input, page: $page) {
      totalItems
      items {
        ... on Content {
          id
          slug
          type
          title
          description
          link
          thumbnail
        }
      }
    }
  }
`

interface SearchQueryResponse {
  search: {
    totalItems: number
    items: Array<{
      title: string
      description?: string | null
      type: string
      thumbnail?: string | null
      link: string
    }>
  }
}

export const useNavbarCardsQuery: UseNavbarCardsQuery = (queryConfig) => {
  const { limit, filters } = queryConfig
  const [items, setItems] = useState<ContentMenuCardData[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isError, setIsError] = useState(false)

  useEffect(() => {
    let cancelled = false

    const fetchCards = async () => {
      setIsLoading(true)
      setIsError(false)

      try {
        const data = await graphQLClient.request<SearchQueryResponse>(
          SEARCH_QUERY,
          {
            ...buildSearchInput({ filters }),
            page: {
              limit,
              offset: 0,
            },
          }
        )

        if (cancelled) {
          return
        }

        setItems(
          data.search.items.map((item) => ({
            title: item.title,
            description: item.description || '',
            imageSrc: handleThumbnail({
              contentType: item.type,
              thumbnail: item.thumbnail || '',
            }),
            href: item.link,
          }))
        )
      } catch (error) {
        console.error(error)
        Sentry.captureException(error)

        if (!cancelled) {
          setIsError(true)
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false)
        }
      }
    }

    fetchCards()

    return () => {
      cancelled = true
    }
  }, [limit, filters])

  return { items, isLoading, isError }
}
